import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { onValue, ref, set } from 'firebase/database';
import { database } from '../../firebase';
import { ANALYTICS_SESSION_CACHE_INVALIDATED_EVENT, atsAnalyticsApi } from '../../services/atsAnalyticsService';
import { ANALYTICS_DATA_STATUS } from './visuals/visualDataModel';

const clone = (value) => JSON.parse(JSON.stringify(value));

const readSessionCache = (key, fallbackValue) => {
  if (typeof window === 'undefined' || !window.sessionStorage) {
    return fallbackValue;
  }

  try {
    const raw = window.sessionStorage.getItem(key);
    return raw ? JSON.parse(raw) : fallbackValue;
  } catch (error) {
    return fallbackValue;
  }
};

const writeSessionCache = (key, value) => {
  if (typeof window === 'undefined' || !window.sessionStorage) {
    return;
  }

  try {
    window.sessionStorage.setItem(key, JSON.stringify(value));
  } catch (error) {
    // Ignore session cache write failures.
  }
};

const filterObjectByKeys = (input = {}, allowedKeys = []) =>
  Object.entries(input).reduce((accumulator, [key, value]) => {
    if (allowedKeys.includes(key)) {
      accumulator[key] = value;
    }
    return accumulator;
  }, {});

export const buildAnalyticsDashboardPath = (companyUID, screenId) =>
  `companies/${companyUID}/analytics_dashboards/${screenId}`;

export const buildAnalyticsWidgetDataPath = (companyUID, screenId, widgetId) =>
  `companies/${companyUID}/analytics_data/${screenId}/${widgetId}`;

export const buildAnalyticsScreenFiltersPath = (companyUID, screenId) =>
  `companies/${companyUID}/analytics_data/${screenId}/screen_filters`;

const buildAnalyticsWidgetSessionCacheKey = (companyUID, screenId) =>
  `analytics-widget-nodes-v1:${companyUID}:${screenId}`;

const buildAnalyticsScreenFiltersSessionCacheKey = (companyUID, screenId) =>
  `analytics-screen-filters-v1:${companyUID}:${screenId}`;

const buildAnalyticsQueriedWidgetSessionCacheKey = (companyUID, screenId) =>
  `analytics-queried-widget-nodes-v1:${companyUID}:${screenId}`;

export const serializeDashboardWidgets = (visibleWidgetIds = [], widgetPrefs = {}) =>
  visibleWidgetIds.map((widgetId, order) => {
    const prefs = widgetPrefs[widgetId] || {};
    const widgetConfig = {
      widgetId,
      order,
      size: prefs.size || 'medium'
    };

    if (prefs.activeGraph) {
      widgetConfig.graphId = prefs.activeGraph;
    }

    if (prefs.selectorState && Object.keys(prefs.selectorState).length) {
      widgetConfig.selectorState = prefs.selectorState;
    }

    return widgetConfig;
  });

export const createDefaultDashboardConfig = ({
  screenId,
  defaultVisibleWidgetIds = [],
  defaultWidgetPrefs = {},
  locked = false,
  updatedBy = 'system'
}) => ({
  schemaVersion: 1,
  screenId,
  initializedFromDefault: true,
  locked,
  updatedAt: Date.now(),
  updatedBy,
  widgets: serializeDashboardWidgets(defaultVisibleWidgetIds, defaultWidgetPrefs)
});

export const deserializeDashboardConfig = ({
  snapshotValue,
  defaultVisibleWidgetIds = [],
  defaultWidgetPrefs = {}
}) => {
  const fallbackState = {
    visibleWidgetIds: [...defaultVisibleWidgetIds],
    widgetPrefs: clone(defaultWidgetPrefs)
  };

  const widgets = Array.isArray(snapshotValue?.widgets) ? snapshotValue.widgets : [];
  if (!widgets.length) {
    return fallbackState;
  }

  const orderedWidgets = [...widgets]
    .filter((widget) => widget && typeof widget.widgetId === 'string' && widget.widgetId)
    .sort((left, right) => (left.order ?? 0) - (right.order ?? 0));

  if (!orderedWidgets.length) {
    return fallbackState;
  }

  const visibleWidgetIds = orderedWidgets.map((widget) => widget.widgetId);
  const widgetPrefs = clone(defaultWidgetPrefs);

  orderedWidgets.forEach((widget) => {
    widgetPrefs[widget.widgetId] = {
      ...(widgetPrefs[widget.widgetId] || {}),
      ...(widget.size ? { size: widget.size } : {}),
      ...(widget.graphId ? { activeGraph: widget.graphId } : {}),
      ...(widget.selectorState ? { selectorState: widget.selectorState } : {})
    };
  });

  return {
    visibleWidgetIds,
    widgetPrefs
  };
};

const createDefaultDashboardState = (defaultVisibleWidgetIds = [], defaultWidgetPrefs = {}) => ({
  visibleWidgetIds: [...defaultVisibleWidgetIds],
  widgetPrefs: clone(defaultWidgetPrefs)
});

const applySelectedViewIdToDatasets = (datasets = {}, selectedViewId = '') => {
  if (!selectedViewId || !datasets || typeof datasets !== 'object') {
    return datasets;
  }

  return Object.entries(datasets).reduce((accumulator, [graphId, dataset]) => {
    if (dataset?.views && typeof dataset.views === 'object' && dataset.views[selectedViewId]) {
      accumulator[graphId] = {
        ...dataset,
        selectedViewId
      };
      return accumulator;
    }

    accumulator[graphId] = dataset;
    return accumulator;
  }, {});
};

const _freshnessLabel = (computedAtMs, isRTL = false) => {
  if (!computedAtMs) return null;
  const ageMs = Date.now() - computedAtMs;
  const ageMin = Math.floor(ageMs / 60000);
  if (ageMin < 2) return isRTL ? 'محدّث للتو' : 'Updated just now';
  if (ageMin < 60) return isRTL ? `محدّث منذ ${ageMin} د` : `Updated ${ageMin}m ago`;
  const ageH = Math.floor(ageMin / 60);
  if (ageH < 24) return isRTL ? `محدّث منذ ${ageH} س` : `Updated ${ageH}h ago`;
  const ageD = Math.floor(ageH / 24);
  return isRTL ? `محدّث منذ ${ageD} يوم` : `Updated ${ageD}d ago`;
};

export const mergeAnalyticsWidgetNode = (widgetNode, fallbackBundle, options = {}) => {
  if (!widgetNode || typeof widgetNode !== 'object') {
    return fallbackBundle;
  }

  const payload = widgetNode.payload || {};
  const status = widgetNode.status || '';
  const hasPayloadDatasets = payload.datasets && typeof payload.datasets === 'object' && Object.keys(payload.datasets).length;
  const baseDatasets = hasPayloadDatasets
    ? payload.datasets
    : status && status !== ANALYTICS_DATA_STATUS.READY
      ? {}
      : fallbackBundle.datasets;
  const datasets = applySelectedViewIdToDatasets(baseDatasets, options.selectedViewId || '');

  const rawAnnotations = Array.isArray(payload.annotationItems) ? payload.annotationItems : (fallbackBundle.annotationItems || []);
  const normalizedAnnotations = rawAnnotations.map((a) =>
    typeof a === 'string' ? a : (options.isRTL ? (a?.labelAr || a?.label || '') : (a?.label || ''))
  ).filter(Boolean);
  const computedAt = widgetNode.freshness?.computedAt;
  const freshnessLabel = _freshnessLabel(computedAt, options.isRTL);
  const annotationItems = freshnessLabel
    ? [...normalizedAnnotations.filter((a) => !a.match(/^Updated |^محدّث/)), freshnessLabel]
    : normalizedAnnotations;

  return {
    ...fallbackBundle,
    dataStatus:
      status ||
      (datasets && Object.keys(datasets).length ? ANALYTICS_DATA_STATUS.READY : fallbackBundle.dataStatus),
    title: payload.title || fallbackBundle.title,
    annotationItems,
    datasets,
    supportedFilters: Array.isArray(widgetNode.supportedFilters)
      ? widgetNode.supportedFilters
      : (fallbackBundle.supportedFilters || []),
    supportedGroupBy: Array.isArray(widgetNode.supportedGroupBy)
      ? widgetNode.supportedGroupBy
      : (fallbackBundle.supportedGroupBy || []),
    supportedGraphs: Array.isArray(widgetNode.supportedGraphs)
      ? widgetNode.supportedGraphs
      : (fallbackBundle.supportedGraphs || [])
  };
};

export const createUnavailableAnalyticsBundle = ({
  title = '',
  annotationItems = [],
  supportedFilters = [],
  supportedGroupBy = [],
  supportedGraphs = []
} = {}) => ({
  title,
  annotationItems,
  dataStatus: ANALYTICS_DATA_STATUS.UNSUPPORTED,
  datasets: {},
  supportedFilters,
  supportedGroupBy,
  supportedGraphs
});

export const useSharedAnalyticsDashboard = ({
  companyUID = '',
  screenId,
  defaultVisibleWidgetIds = [],
  defaultWidgetPrefs = {},
  updatedBy = 'system',
  locked = false
}) => {
  const defaultState = useMemo(
    () => createDefaultDashboardState(defaultVisibleWidgetIds, defaultWidgetPrefs),
    [defaultVisibleWidgetIds, defaultWidgetPrefs]
  );
  const [dashboardState, setDashboardState] = useState(defaultState);
  const [dashboardReady, setDashboardReady] = useState(!companyUID);
  const seedInFlightRef = useRef(false);

  const persistDashboardState = useCallback(
    async (nextState) => {
      if (!companyUID || !screenId) return;

      const dashboardRef = ref(database, buildAnalyticsDashboardPath(companyUID, screenId));
      await set(
        dashboardRef,
        createDefaultDashboardConfig({
          screenId,
          defaultVisibleWidgetIds: nextState.visibleWidgetIds,
          defaultWidgetPrefs: nextState.widgetPrefs,
          locked,
          updatedBy
        })
      );
    },
    [companyUID, locked, screenId, updatedBy]
  );

  const replaceDashboardState = useCallback(
    (nextVisibleWidgetIds, nextWidgetPrefs) => {
      const nextState = {
        visibleWidgetIds: [...nextVisibleWidgetIds],
        widgetPrefs: clone(nextWidgetPrefs)
      };
      setDashboardState(nextState);
      void persistDashboardState(nextState);
    },
    [persistDashboardState]
  );

  const mutateDashboardState = useCallback(
    (updater) => {
      setDashboardState((current) => {
        const nextState = updater(current);
        void persistDashboardState(nextState);
        return nextState;
      });
    },
    [persistDashboardState]
  );

  const resetDashboardState = useCallback(() => {
    const nextState = createDefaultDashboardState(defaultVisibleWidgetIds, defaultWidgetPrefs);
    setDashboardState(nextState);
    void persistDashboardState(nextState);
  }, [defaultVisibleWidgetIds, defaultWidgetPrefs, persistDashboardState]);

  const saveDashboardState = useCallback(() => {
    void persistDashboardState(dashboardState);
  }, [dashboardState, persistDashboardState]);

  useEffect(() => {
    if (!companyUID || !screenId) {
      setDashboardState(defaultState);
      setDashboardReady(true);
      return undefined;
    }

    setDashboardReady(false);
    const dashboardRef = ref(database, buildAnalyticsDashboardPath(companyUID, screenId));

    const unsubscribe = onValue(
      dashboardRef,
      (snapshot) => {
        if (!snapshot.exists()) {
          setDashboardState(defaultState);
          setDashboardReady(true);

          if (!seedInFlightRef.current) {
            seedInFlightRef.current = true;
            void set(
              dashboardRef,
              createDefaultDashboardConfig({
                screenId,
                defaultVisibleWidgetIds,
                defaultWidgetPrefs,
                locked,
                updatedBy
              })
            ).finally(() => {
              seedInFlightRef.current = false;
            });
          }
          return;
        }

        seedInFlightRef.current = false;
        setDashboardState(
          deserializeDashboardConfig({
            snapshotValue: snapshot.val(),
            defaultVisibleWidgetIds,
            defaultWidgetPrefs
          })
        );
        setDashboardReady(true);
      },
      () => {
        setDashboardState(defaultState);
        setDashboardReady(true);
      }
    );

    return () => unsubscribe();
  }, [
    companyUID,
    defaultState,
    defaultVisibleWidgetIds,
    defaultWidgetPrefs,
    locked,
    screenId,
    updatedBy
  ]);

  return {
    ...dashboardState,
    dashboardReady,
    replaceDashboardState,
    mutateDashboardState,
    resetDashboardState,
    saveDashboardState
  };
};

export const useAnalyticsWidgetData = ({
  companyUID = '',
  screenId,
  widgetIds = []
}) => {
  const widgetIdsKey = widgetIds.join('|');
  const cacheKey = useMemo(
    () => (companyUID && screenId ? buildAnalyticsWidgetSessionCacheKey(companyUID, screenId) : ''),
    [companyUID, screenId]
  );
  const [widgetNodes, setWidgetNodes] = useState(() =>
    cacheKey ? filterObjectByKeys(readSessionCache(cacheKey, {}), widgetIds) : {}
  );
  const [loading, setLoading] = useState(Boolean(companyUID && screenId && widgetIds.length && !Object.keys(widgetNodes).length));

  useEffect(() => {
    if (!cacheKey) {
      setWidgetNodes((current) => (Object.keys(current).length ? {} : current));
      setLoading(false);
      return undefined;
    }

    const cachedNodes = filterObjectByKeys(readSessionCache(cacheKey, {}), widgetIds);
    setWidgetNodes((current) => (
      JSON.stringify(current) === JSON.stringify(cachedNodes) ? current : cachedNodes
    ));
    setLoading(Boolean(companyUID && screenId && widgetIds.length && !Object.keys(cachedNodes).length));

    if (!companyUID || !screenId || !widgetIds.length) {
      setLoading(false);
      return undefined;
    }

    let remaining = widgetIds.length;
    const unsubscribers = widgetIds.map((widgetId) =>
      onValue(
        ref(database, buildAnalyticsWidgetDataPath(companyUID, screenId, widgetId)),
        (snapshot) => {
          setWidgetNodes((current) => {
            const nextState = {
              ...current,
              [widgetId]: snapshot.exists() ? snapshot.val() : null
            };
            writeSessionCache(cacheKey, nextState);
            return nextState;
          });
          remaining -= 1;
          if (remaining <= 0) {
            setLoading(false);
          }
        },
        () => {
          setWidgetNodes((current) => {
            const nextState = {
              ...current,
              [widgetId]: null
            };
            writeSessionCache(cacheKey, nextState);
            return nextState;
          });
          remaining -= 1;
          if (remaining <= 0) {
            setLoading(false);
          }
        }
      )
    );

    return () => {
      unsubscribers.forEach((unsubscribe) => unsubscribe());
    };
  }, [cacheKey, companyUID, screenId, widgetIdsKey]);

  return {
    widgetNodes,
    loading
  };
};

export const useAnalyticsScreenFilterOptions = ({
  companyUID = '',
  screenId
}) => {
  const cacheKey = useMemo(
    () => (companyUID && screenId ? buildAnalyticsScreenFiltersSessionCacheKey(companyUID, screenId) : ''),
    [companyUID, screenId]
  );
  const [screenFilterNode, setScreenFilterNode] = useState(() => (cacheKey ? readSessionCache(cacheKey, null) : null));
  const [loading, setLoading] = useState(Boolean(companyUID && screenId && !screenFilterNode));

  useEffect(() => {
    if (!companyUID || !screenId) {
      setScreenFilterNode(null);
      setLoading(false);
      return undefined;
    }
    let cancelled = false;
    if (cacheKey) {
      const cachedNode = readSessionCache(cacheKey, null);
      setScreenFilterNode(cachedNode);
      setLoading(!cachedNode);
    }

    const unsubscribe = onValue(
      ref(database, buildAnalyticsScreenFiltersPath(companyUID, screenId)),
      (snapshot) => {
        if (snapshot.exists()) {
          const nextValue = snapshot.val();
          setScreenFilterNode(nextValue);
          if (cacheKey) {
            writeSessionCache(cacheKey, nextValue);
          }
          setLoading(false);
          return;
        }

        setScreenFilterNode(null);
        void atsAnalyticsApi
          .queryDashboardScreenFilters(companyUID, screenId)
          .then((response) => {
            if (!cancelled) {
              const nextValue = response?.screenFilters || null;
              setScreenFilterNode(nextValue);
              if (cacheKey && nextValue) {
                writeSessionCache(cacheKey, nextValue);
              }
              setLoading(false);
            }
          })
          .catch(() => {
            if (!cancelled) {
              setScreenFilterNode(null);
              setLoading(false);
            }
          });
      },
      () => {
        setScreenFilterNode(null);
        setLoading(false);
      }
    );

    return () => {
      cancelled = true;
      unsubscribe();
    };
  }, [companyUID, screenId]);

  return {
    screenFilterNode,
    loading
  };
};

export const useQueriedAnalyticsWidgetData = ({
  companyUID = '',
  screenId,
  widgetQueryMap = {}
}) => {
  const cacheKey = useMemo(
    () => (companyUID && screenId ? buildAnalyticsQueriedWidgetSessionCacheKey(companyUID, screenId) : ''),
    [companyUID, screenId]
  );
  const [widgetNodes, setWidgetNodes] = useState({});
  const [loading, setLoading] = useState(false);
  const [invalidationTick, setInvalidationTick] = useState(0);
  const widgetQueryKey = useMemo(() => JSON.stringify(widgetQueryMap || {}), [widgetQueryMap]);
  const queryEntries = useMemo(
    () => Object.entries(widgetQueryMap).filter(([, config]) => config?.enabled),
    [widgetQueryKey]
  );
  const queryKey = useMemo(() => JSON.stringify(queryEntries), [queryEntries]);

  const getQueryEntryCacheId = useCallback(
    (widgetId, config) => `${widgetId}::${JSON.stringify({
      filters: config?.filters || {},
      groupBy: config?.groupBy || '',
      revision: config?.revision || ''
    })}`,
    []
  );

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    const handleInvalidation = (event) => {
      const invalidatedCompanyUid = event?.detail?.companyUid || '';
      if (!invalidatedCompanyUid || invalidatedCompanyUid === companyUID) {
        setInvalidationTick((current) => current + 1);
      }
    };
    window.addEventListener(ANALYTICS_SESSION_CACHE_INVALIDATED_EVENT, handleInvalidation);
    return () => {
      window.removeEventListener(ANALYTICS_SESSION_CACHE_INVALIDATED_EVENT, handleInvalidation);
    };
  }, [companyUID]);

  useEffect(() => {
    if (!companyUID || !screenId || !queryEntries.length) {
      setWidgetNodes((current) => (Object.keys(current).length ? {} : current));
      setLoading(false);
      return undefined;
    }

    let cancelled = false;
    const cachedMap = cacheKey ? readSessionCache(cacheKey, {}) : {};
    const cachedNodes = queryEntries.reduce((accumulator, [widgetId, config]) => {
      const cacheEntryId = getQueryEntryCacheId(widgetId, config);
      const cachedValue = cachedMap[cacheEntryId];
      if (cachedValue && typeof cachedValue === 'object') {
        accumulator[widgetId] = cachedValue;
      }
      return accumulator;
    }, {});
    setWidgetNodes(cachedNodes);
    setLoading(Object.keys(cachedNodes).length < queryEntries.length);

    const run = async () => {
      try {
        const responses = await Promise.all(
          queryEntries.map(([widgetId, config]) =>
            atsAnalyticsApi
              .queryDashboardWidget(companyUID, screenId, widgetId, config.filters || {}, config.groupBy || '', {
                revision: config.revision || '',
                forceRefresh: config.forceRefresh === true,
                staleMs: config.staleMs
              })
              .then((response) => [widgetId, response?.widget || null])
              .catch(() => [widgetId, null])
          )
        );

        if (cancelled) return;

        const nextNodes = responses.reduce((accumulator, [widgetId, widget]) => {
          accumulator[widgetId] = widget;
          return accumulator;
        }, {});
        setWidgetNodes(nextNodes);
        if (cacheKey) {
          const nextCacheMap = { ...(readSessionCache(cacheKey, {}) || {}) };
          queryEntries.forEach(([widgetId, config]) => {
            nextCacheMap[getQueryEntryCacheId(widgetId, config)] = nextNodes[widgetId] ?? null;
          });
          writeSessionCache(cacheKey, nextCacheMap);
        }
        setLoading(false);
      } catch (error) {
        if (!cancelled) {
          setWidgetNodes({});
          setLoading(false);
        }
      }
    };

    void run();

    return () => {
      cancelled = true;
    };
  }, [companyUID, invalidationTick, queryKey, queryEntries, screenId]);

  return {
    widgetNodes,
    loading
  };
};
